/*
 * *****************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Mon, Jul 19th 2021, 3:32:30 pm
 *
 * *****************************************************
 */
import { generatePath } from 'react-router-dom';

import { emptyAPI } from 'states/services'; // Import empty api
import { Workspace } from 'states/slices/workspaces/types';

import * as authEndpoints from './endpoints';
import {
  ConfirmEmailRequest,
  AuthenticationEmailPasswordRequest,
  AuthenticationGoogleRequest,
  CreateWorkspacesRequest,
  AuthenticationResponse
} from './types';

/**
 * An authentication api
 * create by extending (injecting) empty api with authentication endpoints
 */
export const authAPI = emptyAPI.injectEndpoints({
  endpoints: builder => ({
    refreshToken: builder.query<AuthenticationResponse, void>({
      query: () => authEndpoints.REFRESH_TOKEN_ENDPOINT.url,
      extraOptions: {
        isPrivate: authEndpoints.REFRESH_TOKEN_ENDPOINT.isPrivate
      },
      transformResponse: (response: { data: AuthenticationResponse }) => response.data
    }),
    signInWithEmailPassword: builder.mutation<void, AuthenticationEmailPasswordRequest>({
      query: credentials => ({
        url: authEndpoints.SIGN_IN_ENDPOINT.url,
        method: 'POST',
        body: credentials
      }),
      extraOptions: {
        isPrivate: authEndpoints.SIGN_IN_ENDPOINT.isPrivate
      }
    }),
    signUpWithEmailPassword: builder.mutation<void, AuthenticationEmailPasswordRequest>({
      query: credentials => ({
        url: authEndpoints.SIGN_UP_ENDPOINT.url,
        method: 'POST',
        body: credentials
      }),
      extraOptions: {
        isPrivate: authEndpoints.SIGN_UP_ENDPOINT.isPrivate
      }
    }),
    signInWithGoogle: builder.mutation<AuthenticationResponse, AuthenticationGoogleRequest>({
      query: credentials => ({
        url: authEndpoints.SIGN_IN_ENDPOINT.url,
        method: 'POST',
        body: credentials
      }),
      extraOptions: {
        isPrivate: authEndpoints.SIGN_IN_ENDPOINT.isPrivate
      },
      transformResponse: (response: { data: AuthenticationResponse }) => response.data
    }),
    signUpWithGoogle: builder.mutation<AuthenticationResponse, AuthenticationGoogleRequest>({
      query: credentials => ({
        url: authEndpoints.SIGN_UP_ENDPOINT.url,
        method: 'POST',
        body: credentials
      }),
      extraOptions: {
        isPrivate: authEndpoints.SIGN_UP_ENDPOINT.isPrivate
      },
      transformResponse: (response: { data: AuthenticationResponse }) => response.data
    }),
    confirmEmailSignIn: builder.mutation<AuthenticationResponse, ConfirmEmailRequest>({
      query: body => ({
        url: authEndpoints.CONFIRM_EMAIL_SIGN_IN_ENDPOINT.url,
        method: 'POST',
        body
      }),
      extraOptions: {
        isPrivate: authEndpoints.CONFIRM_EMAIL_SIGN_IN_ENDPOINT.isPrivate
      },
      transformResponse: (response: { data: AuthenticationResponse }) => response.data
    }),
    confirmEmailSignUp: builder.mutation<AuthenticationResponse, ConfirmEmailRequest>({
      query: body => ({
        url: authEndpoints.CONFIRM_EMAIL_SIGN_UP_ENDPOINT.url,
        method: 'POST',
        body
      }),
      extraOptions: {
        isPrivate: authEndpoints.CONFIRM_EMAIL_SIGN_UP_ENDPOINT.isPrivate
      },
      transformResponse: (response: { data: AuthenticationResponse }) => response.data
    }),
    getWorkspaces: builder.query<Workspace[], void>({
      query: () => authEndpoints.WORKSPACES_ENDPOINT.url,
      extraOptions: {
        isPrivate: authEndpoints.WORKSPACES_ENDPOINT.isPrivate
      },
      transformResponse: (response: { data: Workspace[] }) => response.data
    }),
    createWorkspace: builder.mutation<void, CreateWorkspacesRequest>({
      query: body => ({
        url: authEndpoints.WORKSPACES_ENDPOINT.url,
        method: 'POST',
        body
      }),
      extraOptions: {
        isPrivate: authEndpoints.WORKSPACES_ENDPOINT.isPrivate
      }
    }),
    signInToWorkspace: builder.query<void, string>({
      query: workspaceId =>
        generatePath(authEndpoints.WORKSPACE_SIGN_IN_ENDPOINT.url, { workspaceId }),
      extraOptions: {
        isPrivate: authEndpoints.WORKSPACE_SIGN_IN_ENDPOINT.isPrivate
      }
    })
  }),
  overrideExisting: true
});
